import { useRef, useCallback, useEffect } from "react";
import { useWebSocket, type ServerMessage } from "@/hooks/useWebSocket";
import { useConcierge } from "@/hooks/useConcierge";

export type { ServerMessage } from "@/hooks/useWebSocket";
export type { DebugSummary } from "@/hooks/useWebSocket";
export type { SuggestionItem } from "@/hooks/useWebSocket";

type ConnectionMode = "concierge" | "project";

interface UseChatConnectionOptions {
  activeProjectId: string | null;
  onMessage: (msg: ServerMessage) => void;
}

interface UseChatConnectionReturn {
  state: "connecting" | "connected" | "disconnected";
  agentConnected: boolean;
  agentWaking: boolean;
  agentReason: string | null;
  connect: () => void;
  disconnect: () => void;
  sendChat: (content: string, sessionId?: string) => void;
  sendAction: (action: string, payload: Record<string, unknown>) => void;
  mode: ConnectionMode;
}

export function useChatConnection({
  activeProjectId,
  onMessage,
}: UseChatConnectionOptions): UseChatConnectionReturn {
  const mode: ConnectionMode = activeProjectId ? "project" : "concierge";
  const prevModeRef = useRef<ConnectionMode>(mode);
  const prevProjectIdRef = useRef<string | null>(activeProjectId);

  // Both hooks are always instantiated (React rules), but only the active one connects.
  // We pass a dummy projectId to useWebSocket when in concierge mode — it won't connect
  // because we never call its connect().
  const ws = useWebSocket({
    projectId: activeProjectId ?? "__inactive__",
    onMessage,
  });

  const concierge = useConcierge({
    onMessage: onMessage as (msg: unknown) => void,
  });

  // When mode or projectId changes, disconnect the old and connect the new
  useEffect(() => {
    const prevMode = prevModeRef.current;
    const prevProjectId = prevProjectIdRef.current;
    prevModeRef.current = mode;
    prevProjectIdRef.current = activeProjectId;

    // Detect actual change
    if (prevMode === mode && prevProjectId === activeProjectId) return;

    // Disconnect both hooks to prevent stale reconnection from onclose handlers
    ws.disconnect();
    concierge.disconnect();

    // Connect the newly active hook (small delay to let disconnect + onclose settle)
    const timer = setTimeout(() => {
      // Re-disconnect the inactive hook to clear any reconnect timer set by
      // an async onclose handler that fired after our initial disconnect()
      if (mode === "project") {
        concierge.disconnect();
        ws.connect();
      } else {
        ws.disconnect();
        concierge.connect();
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [mode, activeProjectId]);

  // Initial connect on mount
  useEffect(() => {
    if (mode === "project") {
      ws.connect();
    } else {
      concierge.connect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendChat = useCallback(
    (content: string, sessionId?: string) => {
      if (mode === "project" && sessionId) {
        ws.sendChat(content, sessionId);
      } else if (mode === "concierge") {
        concierge.send(content);
      }
    },
    [mode, ws.sendChat, concierge.send],
  );

  const sendAction = useCallback(
    (action: string, payload: Record<string, unknown>) => {
      if (mode === "project") {
        ws.sendAction(action, payload);
      } else {
        concierge.sendAction(action, payload);
      }
    },
    [mode, ws.sendAction, concierge.sendAction],
  );

  const connect = useCallback(() => {
    if (mode === "project") {
      ws.connect();
    } else {
      concierge.connect();
    }
  }, [mode, ws.connect, concierge.connect]);

  const disconnect = useCallback(() => {
    if (mode === "project") {
      ws.disconnect();
    } else {
      concierge.disconnect();
    }
  }, [mode, ws.disconnect, concierge.disconnect]);

  if (mode === "project") {
    return {
      state: ws.state,
      agentConnected: ws.agentConnected,
      agentWaking: ws.agentWaking,
      agentReason: ws.agentReason,
      connect,
      disconnect,
      sendChat,
      sendAction,
      mode,
    };
  }

  return {
    state: concierge.state,
    agentConnected: false,
    agentWaking: false,
    agentReason: null as string | null,
    connect,
    disconnect,
    sendChat,
    sendAction,
    mode,
  };
}
